import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges} from '@angular/core';
import {FilterGroup, FilterGroupType} from "../../filter-panel/filter-panel.component";
import {
  CategoryItem,
  Country,
  Gender,
  LeagueCompetitorStanding,
  LeagueEvents,
  LeagueRaceType,
  LeagueStandingsFilter,
  LeagueStandingsMeta,
  LeagueStandingsViewMode,
  PagedFilter,
  Pages,
} from "@model";
import {TranslocoService} from "@ngneat/transloco";
import {LeagueStandingsService} from "@services";
import {FilterItem} from "../../filter-item/filter-item.component";
import {getLanguageValue, getMultiLanguageValue} from "@utility";
import {PagingComponent} from "../../paging.component";
import {Observable} from "rxjs";

@Component({
  selector: 'app-league-results-table',
  templateUrl: './league-results-table.component.html',
  styleUrl: './league-results-table.component.scss',
  host: { ngSkipHydration: 'true' },
  standalone: false
})
export class LeagueResultsTableComponent extends PagingComponent<LeagueCompetitorStanding> implements OnInit, OnChanges {
  @Input() isAdmin: boolean = false;
  @Input({required: true}) leagueId!: number;
  @Input() categoryItems: CategoryItem[] = [];
  @Input() countries: Country[] = [];
  @Input() viewMode: LeagueStandingsViewMode = LeagueStandingsViewMode.PUBLIC;
  @Input() standingsMeta: LeagueStandingsMeta | undefined;
  @Input() availableRaceTypes: LeagueRaceType[] = [];
  @Input() leagueEvents?: LeagueEvents;

  @Output() onRaceTypeChange = new EventEmitter<LeagueRaceType>();

  results: LeagueCompetitorStanding[] = [];
  roundColumns: { id: number, name: string, round: number, start: Date | undefined, raceIds: number[] }[] = [];
  raceType: LeagueRaceType = LeagueRaceType.INDIVIDUAL;
  raceTypesFilterItems: FilterItem[] = [];
  filterGroups: FilterGroup[] = [];
  filter: LeagueStandingsFilter = new LeagueStandingsFilter();

  constructor(
      private translocoService: TranslocoService,
      private leagueStandingsService: LeagueStandingsService) {
    super();
  }

  override ngOnInit() {
    super.ngOnInit()
    this.initFromMeta();
    this.initRaceTypes();
  }

  ngOnChanges(ch: SimpleChanges): void {
    if (ch['standingsMeta'] && !ch['standingsMeta'].firstChange) {
      this.initFromMeta();
      this.initRaceTypes();
    }
  }

  private initFromMeta() {
    this.raceType = this.standingsMeta?.raceType ?? LeagueRaceType.INDIVIDUAL;
    this.viewMode = this.standingsMeta?.metaType ?? this.viewMode;
    this.filter = new LeagueStandingsFilter(
      this.viewMode, undefined, Gender.MALE, undefined, this.raceType
    );
    this.setRoundColumns();
    this.initRaceTypes();
    this.buildFilteringParams(this.filter);
    this.onExplore();
  }

  setRaceType(raceType: LeagueRaceType) {
      const selected: LeagueRaceType = raceType === LeagueRaceType.TEAM.valueOf()
          ? LeagueRaceType.TEAM
          : LeagueRaceType.INDIVIDUAL;
      if (this.raceType !== selected) {
        this.onRaceTypeChange.emit(selected);
      }
      this.results = [];
  }

  private setRoundColumns() {
    this.roundColumns = [];
    const roundMap = new Map<number, { id: number, name: string, round: number, start: Date | undefined, raceIds: number[] }>();
    const lang = this.translocoService.getActiveLang();
    if (this.viewMode === LeagueStandingsViewMode.PUBLIC && this.leagueEvents?.getAllEvents()?.length) {
      this.leagueEvents?.getAllEvents().forEach(e => {
          if (e.id == null || !e.round) return;
          if (!roundMap.has(e.id!)) {
            const shortName: string | undefined = getMultiLanguageValue(e.nameShort, this.translocoService.getActiveLang());
            const eventName: string | undefined = getLanguageValue(
              e.name, e.nameEng, e.nameIta,
              e.nameSlo, e.nameSr, e.nameBs,
              this.translocoService.getActiveLang())
            roundMap.set(e.id!, {
              id: e.id!,
              name: shortName ?? (eventName ?? "-") ,
              round: e.round ?? 0,
              start: e.start,
              raceIds: []
            });
        }
          e.racesList?.forEach(r => {
            roundMap.get(e.id!)!.raceIds.push(r.id!);
          })
      })
    }
    else {
      (this.standingsMeta?.racesApplied ?? []).forEach(r => {
        if (r.eventId == null) return;
        if (!roundMap.has(r.eventId)) {
          roundMap.set(r.eventId, {
            id: r.eventId,
            name: getMultiLanguageValue(r.eventName, lang) ?? '-',
            round: r.round ?? 0,
            start: r.eventStart,
            raceIds: []
          });
        }
        roundMap.get(r.eventId)!.raceIds.push(r.raceId!);
      });
    }
    this.roundColumns = Array.from(roundMap.values()).sort((a, b) => a.round - b.round);
  }

  private buildFilteringParams(filter?: LeagueStandingsFilter) {
    this.filterGroups = [
      ...(this.availableRaceTypes.length > 1 ? [
        new FilterGroup('raceType', 'raceType',
          this.raceType === LeagueRaceType.TEAM ? 'league.standings.type.team' : 'league.standings.type.individual',
          FilterGroupType.SINGLE_SELECT, false,
          this.raceTypesFilterItems, true, true
        )
      ] : []),
      ...(this.raceType === LeagueRaceType.INDIVIDUAL ? [
        new FilterGroup('gender', 'gender', 'app.common.gender',
            FilterGroupType.SINGLE_SELECT, false, [
          new FilterItem(Gender.MALE, undefined, 'app.common.gender.1', filter?.gender === Gender.MALE || !filter?.gender),
          new FilterItem(Gender.FEMALE,undefined, 'app.common.gender.2', filter?.gender === Gender.FEMALE)
        ], true, true)
      ] : []),
      ...(this.raceType === LeagueRaceType.INDIVIDUAL && this.categoryItems.length ? [
        new FilterGroup('category', 'category', 'app.common.category',
            FilterGroupType.SINGLE_SELECT, false,
          this.categoryItems.map(ci => new FilterItem(ci.id, ci.code, undefined, filter?.categoryItemId === ci.id))
        , false, true)
      ] : []),
    ];
  }

  onFilterChange(result: Record<string, any>): void {
    const raceType = result['raceType'];
    if (raceType !== this.raceType) {
      this.setRaceType(raceType)
    }
    else {
      this.results = [];
      this.filter = new LeagueStandingsFilter(
          this.viewMode,
          result['searchTerm'],
          result['gender'],
          result['category'],
          this.raceType
      );
      this.onExplore();
    }
  }

  protected fetchPage(page:number): Observable<LeagueCompetitorStanding[]> {
    const pagedFilter = new PagedFilter(new Pages(undefined, this.page), this.filter);
    return this.leagueStandingsService.getStandings(this.leagueId, pagedFilter, this.isAdmin)
  }

  protected override afterSetItems(items: LeagueCompetitorStanding[]): void {
    this.results = items;
  }

  initRaceTypes() {
    this.raceTypesFilterItems = [];
    this.availableRaceTypes.forEach(type => {
      this.raceTypesFilterItems.push(new FilterItem(type, undefined, "league.standings.type." + type, this.raceType === type))
    })
  }

  protected readonly LeagueRaceType = LeagueRaceType;
}
